/**
 * Nogu — Ask AI: modal recipient picker + template placeholders + encoded prompt.
 */
(function () {
  var FOCUSABLE =
    'button:not([disabled]):not([aria-hidden="true"]), [href]:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var DEFAULT_PROMPT_TEMPLATE =
    'You are a helpful shopping assistant for NOGU, an award-winning Canadian jewelry designer. \n' +
    'I am buying for:\n' +
    '***recipient.context***\n' +
    'Based on this context, write an honest and emotionally compelling explanation of why NOGU could be the better choice for this gift than a generic jewelry brand.\n' +
    'Highlight how the design, meaning, and craftsmanship make the piece feel personal and memorable. Mention NOGU\'s Lifetime Warranty as proof that the gift is made to last, many purchases help support NOGU\'s charitable partners, so the gift carries purpose beyond the jewelry itself.\n' +
    'Use a warm, elegant, confident tone. \n' +
    'Keep the response to 1 to 3 short paragraphs. Make it very easy to read using Markdown, include point forms. Use references to support your points.\n' +
    'I am browsing NOGU\'s website, nogu.studio, on this page: ***context.title***.\n' +
    'Page description:\n' +
    '***page.description***';

  function parseConfig(root) {
    var sid = root.getAttribute('data-section-id');
    if (!sid) return null;
    var el = document.getElementById('nogu-ask-ai-config-' + sid);
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (err) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[nogu-ask-ai] Could not parse section config JSON', sid, err);
      }
      return null;
    }
  }

  function assemblePrompt(cfg, recipientContextValue) {
    var vars = cfg.vars || {};
    var t = (cfg.promptTemplate && String(cfg.promptTemplate).trim()) || DEFAULT_PROMPT_TEMPLATE;

    var merged = {};
    Object.keys(vars).forEach(function (k) {
      merged[k] = vars[k];
    });
    merged['recipient.context'] =
      recipientContextValue == null ? '' : String(recipientContextValue);

    var out = t;
    Object.keys(merged).forEach(function (key) {
      var token = '***' + key + '***';
      var val = merged[key] == null ? '' : String(merged[key]);
      out = out.split(token).join(val);
    });

    if (cfg.extraInstructions) {
      if (out.indexOf('***extra.instructions***') !== -1) {
        out = out.split('***extra.instructions***').join(cfg.extraInstructions);
      } else {
        out += '\n\n' + cfg.extraInstructions;
      }
    }
    return out;
  }

  function buildPrompt(cfg, injectorKey) {
    var inj = cfg.injectors && cfg.injectors[injectorKey];
    if (!inj) return '';
    return assemblePrompt(cfg, inj);
  }

  function getFocusables(modal) {
    return Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE)).filter(function (el) {
      if (el.disabled || el.getAttribute('aria-hidden') === 'true') return false;
      return modal.contains(el);
    });
  }

  function trapFocus(modal, e) {
    if (e.key !== 'Tab') return;
    var list = getFocusables(modal);
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function initRoot(root) {
    if (root.getAttribute('data-nogu-ask-ai-initialized') === 'true') return;

    var cfg = parseConfig(root);
    if (!cfg) return;

    var backdrop = root.querySelector('[data-nogu-ask-ai-backdrop]');
    var modal = root.querySelector('[role="dialog"][aria-modal="true"]');
    var continueBtn = modal && modal.querySelector('[data-nogu-ask-ai-continue]');
    if (!backdrop || !modal || !continueBtn) return;

    var helpEl = modal.querySelector('[data-nogu-ask-ai-modal-help]');
    var previewEl = modal.querySelector('[data-nogu-ask-ai-prompt-preview]');
    var continueLabelEl = modal.querySelector('[data-nogu-ask-ai-continue-label]');
    var cancelBtn = modal.querySelector('[data-nogu-ask-ai-cancel]');
    var closeBtn = modal.querySelector('[data-nogu-ask-ai-close]');
    var radios = modal.querySelectorAll('input[type="radio"][name^="nogu-ask-ai-recipient"]');

    var state = {
      baseUrl: '',
      toolLabel: '',
      lastFocus: null,
    };

    function updatePromptPreview() {
      var selected = modal.querySelector('input[type="radio"][name^="nogu-ask-ai-recipient"]:checked');
      var recipientVal;
      if (selected && cfg.injectors && cfg.injectors[selected.value]) {
        recipientVal = cfg.injectors[selected.value];
      } else {
        recipientVal = cfg.recipientPendingLabel != null ? cfg.recipientPendingLabel : '***recipient.context***';
      }
      if (previewEl) previewEl.textContent = assemblePrompt(cfg, recipientVal);
    }

    function setOpen(open) {
      if (open) {
        backdrop.removeAttribute('hidden');
        modal.removeAttribute('hidden');
        backdrop.setAttribute('aria-hidden', 'false');
        modal.setAttribute('aria-hidden', 'false');
        document.addEventListener('keydown', onDocKeydown);
      } else {
        backdrop.setAttribute('aria-hidden', 'true');
        modal.setAttribute('aria-hidden', 'true');
        backdrop.setAttribute('hidden', '');
        modal.setAttribute('hidden', '');
        document.removeEventListener('keydown', onDocKeydown);
        if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
          state.lastFocus.focus();
        }
        state.lastFocus = null;
      }
    }

    function onDocKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (modal.getAttribute('aria-hidden') === 'false') {
        trapFocus(modal, e);
      }
    }

    function syncContinueDisabled() {
      var selected = modal.querySelector('input[type="radio"][name^="nogu-ask-ai-recipient"]:checked');
      var ok = !!(selected && selected.value);
      continueBtn.disabled = !ok;
      var prefix = cfg.continuePrefix || 'Continue to';
      var labelGo = prefix + ' ' + state.toolLabel;
      if (continueLabelEl) {
        continueLabelEl.textContent = ok ? labelGo : prefix + '…';
      }
      continueBtn.setAttribute(
        'aria-label',
        ok ? labelGo : 'Choose who you are buying for, then continue to ' + state.toolLabel
      );
      updatePromptPreview();
    }

    function openFromTrigger(trigger) {
      state.baseUrl = trigger.getAttribute('data-base-url') || '';
      state.toolLabel = trigger.getAttribute('data-tool-label') || 'assistant';
      state.lastFocus = document.activeElement;

      if (helpEl) {
        helpEl.textContent =
          'Choose who you are shopping for, then we will open ' +
          state.toolLabel +
          (previewEl
            ? ' in a new tab with the prompt shown below.'
            : ' in a new tab with a tailored prompt.');
      }

      for (var i = 0; i < radios.length; i++) {
        radios[i].checked = false;
      }
      syncContinueDisabled();

      setOpen(true);

      var firstRadio = modal.querySelector('input[type="radio"]');
      window.requestAnimationFrame(function () {
        if (firstRadio) firstRadio.focus();
        else modal.focus();
      });
    }

    function onContinue() {
      var selected = modal.querySelector('input[type="radio"][name^="nogu-ask-ai-recipient"]:checked');
      if (!selected || !state.baseUrl) return;
      var prompt = buildPrompt(cfg, selected.value);
      if (!prompt) return;
      var url = state.baseUrl + encodeURIComponent(prompt);
      var w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w) w.opener = null;
      setOpen(false);
    }

    root.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-nogu-ask-ai-trigger]');
      if (!trigger || !root.contains(trigger)) return;
      e.preventDefault();
      e.stopPropagation();
      openFromTrigger(trigger);
    });

    backdrop.addEventListener('click', function () {
      setOpen(false);
    });

    if (cancelBtn) cancelBtn.addEventListener('click', function () {
      setOpen(false);
    });
    if (closeBtn) closeBtn.addEventListener('click', function () {
      setOpen(false);
    });
    continueBtn.addEventListener('click', onContinue);

    for (var j = 0; j < radios.length; j++) {
      radios[j].addEventListener('change', syncContinueDisabled);
    }

    root.setAttribute('data-nogu-ask-ai-initialized', 'true');
  }

  function boot() {
    document.querySelectorAll('[data-nogu-ask-ai-root]').forEach(initRoot);
  }

  if (!window.__NOGU_ASK_AI_LISTENERS__) {
    window.__NOGU_ASK_AI_LISTENERS__ = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    }
    document.addEventListener('shopify:section:load', boot);
  }

  if (document.readyState !== 'loading') {
    boot();
  }
})();
